/**
 * Entering a delivery, as the counter actually does it.
 *
 * The same fields, the same words and the same rules as the dispatch Shop Entry
 * staff have been filling for months. Nothing here is tidier than what they are
 * used to, on purpose: renaming "Errand/Personal Shopper" to something neater
 * would make every record they enter read differently from the ones already
 * filed beside it.
 *
 * The form fills in the order a job happens — who and where first, then the
 * money, then the rider, then how it ended. A delivery can be saved at any
 * stage, because it usually is: a job is entered when it is booked and finished
 * when it is done.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DELIVERY_PAYMENT_METHODS,
  DELIVERY_PENDING_VERIFICATION,
  DELIVERY_PAYMENT_STATUSES,
  DELIVERY_SERVICE_TYPES,
  DELIVERY_STATUSES,
  PROOF_EXEMPT_METHODS,
  RIDER_REQUIRED_STATUSES,
  type Delivery,
  type DeliveryPriceRule,
  type DeliveryZone,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { currencyPrefix, money } from '../../lib/format';

/** A record the duplicate check found. */
interface DuplicateSide {
  id: string;
  externalRef: string;
  dispatchRef: string | null;
  originSource: string;
  fee: string;
  deliveryStatus: string | null;
  deliveryType: string | null;
}

const SOURCE_WORD: Record<string, string> = {
  whatsapp: 'WhatsApp', customer_app: 'App', shop: 'Shop', manual: 'Counter', app: 'App',
};
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Button, Input, Select, Textarea } from '../../components/ui';
import {
  EMPTY_RIDER,
  RiderFields,
  riderProblems,
  type RiderCompletion,
  type RiderValue,
} from './RiderFields';

/**
 * A tinted group of fields.
 *
 * Three parties appear on one screen and each owns two or three boxes. Without
 * a visual break a counter reads eleven identical inputs and puts the
 * receiver's number in the sender's box, which is the mistake this form exists
 * to stop. The tints are deliberately pale — they separate, they do not shout.
 */
function Section({
  tone,
  title,
  children,
}: {
  tone: 'sender' | 'receiver' | 'rider';
  title: string;
  children: ReactNode;
}): ReactNode {
  const tones = {
    sender: 'border-violet-200 bg-violet-50/60',
    receiver: 'border-sky-200 bg-sky-50/60',
    rider: 'border-emerald-200 bg-emerald-50/60',
  } as const;
  const headings = {
    sender: 'text-violet-900',
    receiver: 'text-sky-900',
    rider: 'text-emerald-900',
  } as const;
  return (
    <fieldset className={`rounded-xl border p-4 ${tones[tone]}`}>
      <legend className={`px-1 text-xs font-semibold uppercase tracking-wide ${headings[tone]}`}>
        {title}
      </legend>
      <div className="space-y-4">{children}</div>
    </fieldset>
  );
}

/** What the business keeps per completed, paid delivery. Mirrors the server. */
const COMPANY_CUT = 500;

/**
 * The approved price for a job today, as the server will judge it.
 *
 * The most specific price that applies today wins — zone at this location,
 * then the zone anywhere, then this location, then business-wide — the same
 * order the server checks in, so the hint never promises a range the save then
 * refuses. A scheduled price does not count until its day.
 */
export function approvedPriceFor(
  rules: DeliveryPriceRule[],
  serviceType: string,
  branchId: string,
  zoneId: string | null = null,
): DeliveryPriceRule | null {
  const forService = rules.filter((r) => r.serviceType === serviceType && r.status === 'current');
  const zoned = zoneId
    ? (forService.find((r) => r.zoneId === zoneId && r.branchId === branchId) ??
      forService.find((r) => r.zoneId === zoneId && r.branchId === null))
    : undefined;
  return (
    zoned ??
    forService.find((r) => r.zoneId === null && r.branchId === branchId) ??
    forService.find((r) => r.zoneId === null && r.branchId === null) ??
    null
  );
}

/** What is still waiting on somebody else after a save, in plain words. */
function waitingOn(saved: Delivery | null): string[] {
  if (!saved) return [];
  const pending = saved.pendingApprovals ?? [];
  const notes: string[] = [];
  if (pending.includes('price')) {
    notes.push('The fee is waiting for approval — nothing is booked until it is approved.');
  }
  if (pending.includes('backdate')) {
    notes.push('The earlier date is waiting for approval — nothing is booked until it is approved.');
  }
  if (pending.includes('payment') || saved.paymentStatus === DELIVERY_PENDING_VERIFICATION) {
    notes.push('Payment is waiting for verification. It is not counted as money until it is checked.');
  }
  return notes;
}

export function DeliveryEntryForm({
  branchId,
  existing,
  onDone,
  onCancel,
}: {
  branchId: string;
  /**
   * The delivery being corrected, when there is one.
   *
   * The same form either way: a job recorded when it was booked and corrected
   * when it is done is one record growing, not two different screens. Absent
   * means a new entry.
   */
  existing?: {
    id: string;
    serviceType: string | null;
    customerName: string | null;
    customerPhone: string | null;
    receiverName: string | null;
    receiverPhone: string | null;
    paidBy: 'sender' | 'receiver' | null;
    pickup: string | null;
    destination: string | null;
    fee: string;
    riderName: string | null;
    riderPhone: string | null;
    riderExternalId: string | null;
    /** The registry rider. Null on records filed before riders were chosen. */
    riderId?: string | null;
    paymentMethod: string | null;
    paymentStatus: string | null;
    deliveryStatus: string | null;
    notes: string | null;
    cancelReason: string | null;
    dispatchRef?: string | null;
    deliveryType?: 'base' | 'fast' | null;
    zoneId?: string | null;
    zoneName?: string | null;
    /** Whether proof of payment is already on file. A new upload replaces it; nothing removes it. */
    hasProof?: boolean;
  };
  onDone: () => void;
  onCancel?: () => void;
}): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'NGN';

  const [form, setForm] = useState({
    serviceType: existing?.serviceType ?? 'Parcel Delivery',
    customerName: existing?.customerName ?? '',
    customerPhone: existing?.customerPhone ?? '',
    receiverName: existing?.receiverName ?? '',
    receiverPhone: existing?.receiverPhone ?? '',
    /*
      Blank on an older delivery that predates the field, so editing one does
      not silently assert a liability nobody recorded. The counter chooses.
    */
    paidBy: existing?.paidBy ?? '',
    pickup: existing?.pickup ?? '',
    destination: existing?.destination ?? '',
    fee: existing?.fee ?? '',
    paymentMethod: existing?.paymentMethod ?? 'Cash',
    paymentStatus: existing?.paymentStatus ?? 'Unpaid',
    /*
      A new job starts Awaiting Rider: the parcel and the money are known
      before the rider is, and a counter should not have to invent one or wait
      to record the delivery at all.
    */
    deliveryStatus: existing?.deliveryStatus ?? 'Awaiting Rider',
    notes: existing?.notes ?? '',
    cancelReason: existing?.cancelReason ?? '',
    dispatchRef: existing?.dispatchRef ?? '',
    deliveryType: (existing?.deliveryType ?? '') as '' | 'base' | 'fast',
    zoneId: existing?.zoneId ?? '',
    priceReason: '',
    dateReason: '',
  });
  /**
   * One per form, not one per tap.
   *
   * A double tap, or a retry after a bad connection, sends the same value, and
   * the server answers with the delivery the first submission made instead of
   * recording the job twice. Correcting a delivery has no use for it.
   */
  const [clientRequestId] = useState(() => crypto.randomUUID());
  /**
   * The rider as the delivery had them, and as the form has them now.
   *
   * Distance and running cost are no longer asked here. Records that have
   * them keep them: a correction sends only what it changes, and those two
   * are never sent.
   */
  const [initialRider] = useState<RiderValue>(() =>
    existing
      ? {
          riderId: existing.riderId ?? null,
          riderExternalId: existing.riderExternalId ?? '',
          riderName: existing.riderName ?? '',
          riderPhone: existing.riderPhone ?? '',
        }
      : EMPTY_RIDER,
  );
  const [rider, setRider] = useState<RiderValue>(initialRider);
  const [riderCompletion, setRiderCompletion] = useState<RiderCompletion | null>(null);
  /**
   * The server's own explanation, once it has asked for a reason.
   *
   * Kept after it is answered so the box does not vanish under the person
   * typing into it when the next save clears the field errors.
   */
  const [priceAsk, setPriceAsk] = useState<string | null>(null);
  const [dateAsk, setDateAsk] = useState<string | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const [proofId, setProofId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  /**
   * The same job, apparently already in the book — found before saving a NEW
   * delivery, so a counter typing a WhatsApp or app order that already synced
   * is asked rather than booking it twice. Saving anyway is their call: two
   * real jobs can look alike. Nothing is changed on the record found.
   */
  const [lookalikes, setLookalikes] = useState<DuplicateSide[] | null>(null);

  const set =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [key]: event.target.value }));

  const fee = Number(form.fee || 0);
  /**
   * Shown, never entered.
   *
   * Staff do not decide what a rider earns, and a field somebody can type into
   * is a figure somebody can get wrong — so the split is displayed as the fee
   * is typed, and the server derives it again rather than trusting this.
   */
  const companyShare = Math.min(COMPANY_CUT, Math.max(0, fee));
  const riderPayout = Math.max(0, fee - companyShare);

  const rulesQuery = useQuery({
    queryKey: ['delivery-price-rules', business?.businessId],
    queryFn: () => api.get<{ data: DeliveryPriceRule[] }>(path('/deliveries/price-rules')),
    enabled: Boolean(business),
  });
  const zonesQuery = useQuery({
    queryKey: ['delivery-zones', business?.businessId],
    queryFn: () => api.get<{ data: DeliveryZone[] }>(path('/deliveries/zones')),
    enabled: Boolean(business),
  });
  /**
   * The zones offered here: this location's own and the business-wide ones.
   * A zone the delivery already names stays on offer even if it has since been
   * retired, so correcting the job does not quietly change its zone.
   */
  const zoneOptions = (zonesQuery.data?.data ?? [])
    .filter((z) => z.branchId === null || z.branchId === branchId)
    .map((z) => ({ value: z.id, label: z.name }));
  if (existing?.zoneId && !zoneOptions.some((o) => o.value === existing.zoneId)) {
    zoneOptions.push({ value: existing.zoneId, label: existing.zoneName ?? 'Current zone' });
  }
  const offersZones = zoneOptions.length > 0;

  const rule = rulesQuery.data
    ? approvedPriceFor(rulesQuery.data.data, form.serviceType, branchId, form.zoneId || null)
    : null;
  const feeTyped = form.fee.trim() !== '';
  const outsideRange =
    feeTyped &&
    (fee === 0 || (rule !== null && (fee < Number(rule.minFee) || fee > Number(rule.maxFee))));
  const showPriceReason = outsideRange || priceAsk !== null;
  const priceHint = !rulesQuery.data
    ? undefined
    : rule
      ? `Approved price: ${currencyPrefix(currency)}${rule.minFee}–${rule.maxFee}${rule.zoneName ? ` in ${rule.zoneName}` : ''}${rule.branchName ? ` at ${rule.branchName}` : ''}`
      : 'No approved price for this job yet — the fee is sent to the owner for approval.';

  const needsRider = RIDER_REQUIRED_STATUSES.includes(form.deliveryStatus);
  const needsProof =
    form.paymentStatus === 'Paid' && !PROOF_EXEMPT_METHODS.includes(form.paymentMethod);
  const isCancelled = ['Cancelled', 'Failed/Returned'].includes(form.deliveryStatus);

  const upload = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      /*
        Whoever keeps the expense files uploads where they always have. A
        counter does not hold that, so it uses the delivery's own proof route
        — which stays switched off until uploads survive a deploy, and then
        says so in words the counter can act on.
      */
      const target = can('expenses:create') ? '/attachments' : '/deliveries/payment-proof';
      const saved = await api.upload<{ id: string }>(path(target), body);
      setProofId(saved.id);
      setFieldErrors((e) => ({ ...e, paymentProofAttachmentId: '' }));
    } catch (error) {
      // The server's reason, where it gave one: a file that is wrong and an
      // upload that is not available yet need different next steps.
      const message = error instanceof ApiError ? error.message : 'That file could not be attached';
      setFieldErrors((e) => ({ ...e, paymentProofAttachmentId: message }));
    } finally {
      setUploading(false);
    }
  };

  const riderChanged =
    rider.riderId !== initialRider.riderId ||
    rider.riderExternalId !== initialRider.riderExternalId ||
    rider.riderName !== initialRider.riderName ||
    rider.riderPhone !== initialRider.riderPhone;

  const save = useMutation({
    mutationFn: async () => {
      // Whatever the rider's record lacked goes onto the record first, so the
      // delivery names a rider the registry fully knows.
      if (riderCompletion) {
        await api.patch(path(`/rider-directory/${riderCompletion.riderId}`), {
          ...(riderCompletion.externalId ? { externalId: riderCompletion.externalId } : {}),
          ...(riderCompletion.phone ? { phone: riderCompletion.phone } : {}),
        });
        setRiderCompletion(null);
        void queryClient.invalidateQueries({ queryKey: ['delivery-riders'] });
      }
      const riderPayload =
        existing && !riderChanged
          ? {}
          : rider.riderId
            ? {
                riderId: rider.riderId,
                riderExternalId: rider.riderExternalId.trim(),
                riderName: rider.riderName.trim(),
                riderPhone: rider.riderPhone.trim(),
              }
            : existing
              ? { riderId: null, riderExternalId: null, riderName: null, riderPhone: null }
              : {};
      const payload = {
        serviceType: form.serviceType,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        // Omitted rather than sent empty when a legacy record leaves them
        // blank: the update schema is partial, so an absent field is untouched
        // while an empty one would overwrite or be refused.
        ...(form.receiverName.trim() ? { receiverName: form.receiverName.trim() } : {}),
        ...(form.receiverPhone.trim() ? { receiverPhone: form.receiverPhone.trim() } : {}),
        ...(form.paidBy ? { paidBy: form.paidBy } : {}),
        pickup: form.pickup.trim(),
        destination: form.destination.trim(),
        fee: form.fee,
        paymentMethod: form.paymentMethod,
        paymentStatus: form.paymentStatus,
        deliveryStatus: form.deliveryStatus,
        ...riderPayload,
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        ...(form.cancelReason.trim() ? { cancelReason: form.cancelReason.trim() } : {}),
        ...(proofId ? { paymentProofAttachmentId: proofId } : {}),
        ...(showPriceReason && form.priceReason.trim()
          ? { priceReason: form.priceReason.trim() }
          : {}),
        ...(dateAsk !== null && form.dateReason.trim() ? { dateReason: form.dateReason.trim() } : {}),
        // A new job names its zone when zones are offered; a correction sends
        // it only when it changed.
        ...(existing
          ? form.zoneId !== (existing.zoneId ?? '')
            ? { zoneId: form.zoneId || null }
            : {}
          : offersZones
            ? { zoneId: form.zoneId || null }
            : {}),
        // Sent when it changed, so a correction can also clear it.
        ...(form.dispatchRef.trim() !== (existing?.dispatchRef ?? '')
          ? { dispatchRef: form.dispatchRef.trim() || null }
          : {}),
        ...(form.deliveryType !== (existing?.deliveryType ?? '')
          ? { deliveryType: form.deliveryType || null }
          : {}),
      };
      // A correction goes to the record it corrects; a new job creates one.
      // `branchId` is not sent on an update: a delivery does not move location,
      // and offering to move it would be offering to file it somewhere it never
      // happened.
      const saved = existing
        ? await api.patch<{ id: string }>(path(`/deliveries/${existing.id}`), payload)
        : await api.post<{ id: string }>(path('/deliveries'), {
            ...payload,
            branchId,
            clientRequestId,
          });
      // Read back what it became, so anything left waiting can be said. The
      // save itself has happened; failing to read it back is not a failed save.
      return api.get<Delivery>(path(`/deliveries/${saved.id}`)).catch(() => null);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      void queryClient.invalidateQueries({ queryKey: ['delivery'] });
      const waiting = waitingOn(saved);
      // Said before leaving, or a counter believes the job is booked when it is not.
      if (waiting.length) setNotices(waiting);
      else onDone();
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields?.['priceReason']) setPriceAsk(caught.message);
        if (caught.fields?.['dateReason']) setDateAsk(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([k, v]) => [k, v[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not save this delivery.');
      }
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    // A correction that leaves the rider alone is not re-asked about them —
    // unless it moves a job with no registry rider to Assigned or beyond,
    // which the server asks about too.
    /*
      The compulsory boxes this form owns, checked before anything is sent.

      The server enforces the same rules — it is the boundary — but a counter
      should not have to wait for a round trip to be told a box is empty.
    */
    const missing: Record<string, string> = {};
    if (!form.customerName.trim()) missing['customerName'] = 'Sender name is required';
    if (!form.customerPhone.trim()) missing['customerPhone'] = 'Sender phone number is required';
    if (!form.pickup.trim()) missing['pickup'] = 'Pick-up location is required';
    if (!form.destination.trim()) missing['destination'] = 'Drop-off location is required';

    /*
      Receiver and payer are compulsory for a NEW delivery, and for an existing
      one that already carries them. They are not demanded from a delivery
      recorded before these fields existed.

      Correcting a typo on a job from last month should not require somebody to
      invent who the parcel was for, or to assert who was going to be billed —
      neither of which is knowable now, and both of which would be a guess
      written into the record. The field stays editable and empty; whoever
      knows can fill it in.
    */
    const predatesReceiver = Boolean(existing) && !existing?.receiverName;
    const predatesPaidBy = Boolean(existing) && !existing?.paidBy;
    if (!predatesReceiver) {
      if (!form.receiverName.trim()) missing['receiverName'] = 'Receiver name is required';
      if (!form.receiverPhone.trim()) missing['receiverPhone'] = 'Receiver phone number is required';
    }
    if (!predatesPaidBy && !form.paidBy) {
      missing['paidBy'] = 'Say who is paying for this delivery';
    }
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      setError('Fill in the fields marked with an asterisk');
      return;
    }

    const becomesAssigned =
      needsRider && !RIDER_REQUIRED_STATUSES.includes(existing?.deliveryStatus ?? '');
    const problems =
      existing && !riderChanged && !(becomesAssigned && !rider.riderId)
        ? {}
        : riderProblems(rider, { required: needsRider });
    if (Object.keys(problems).length) {
      setFieldErrors(problems as Record<string, string>);
      setError('Check the rider details');
      return;
    }
    if (!existing && lookalikes === null) {
      void api
        .post<{ data: DuplicateSide[] }>(path('/deliveries/duplicate-check'), {
          fee: String(form.fee || '0'),
          customerPhone: form.customerPhone.trim() || null,
          receiverPhone: form.receiverPhone.trim() || null,
          dispatchRef: form.dispatchRef.trim() || null,
        })
        .then((r) => {
          if (r.data.length) setLookalikes(r.data);
          else save.mutate();
        })
        // The check is a courtesy; failing to run it must not stop a save.
        .catch(() => save.mutate());
      return;
    }
    save.mutate();
  }

  if (lookalikes && lookalikes.length > 0 && !save.isPending && !save.isSuccess) {
    return (
      <div className="space-y-4" role="alertdialog" aria-label="Possible duplicate">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-sm font-medium text-amber-900">This job may already be recorded</p>
          <p className="mt-1 text-sm text-amber-800">
            Same order number, or the same customer and fee on the same day:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {lookalikes.map((l) => (
              <li key={l.id}>
                <span className="font-mono">{l.dispatchRef ?? l.externalRef}</span> · {SOURCE_WORD[l.originSource] ?? l.originSource} ·{' '}
                {money(l.fee, currency)} · {l.deliveryStatus ?? '—'}
                {l.deliveryType ? ` · ${l.deliveryType === 'fast' ? 'Fast' : 'Base'}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => setLookalikes(null)}>
            Go back
          </Button>
          <Button type="button" onClick={() => save.mutate()}>
            It is a different job — save
          </Button>
        </div>
      </div>
    );
  }

  if (notices.length) {
    return (
      <div className="space-y-4">
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-sm font-medium text-amber-900">Saved.</p>
          {notices.map((n) => (
            <p key={n} className="mt-1 text-sm text-amber-800">
              {n}
            </p>
          ))}
        </div>
        <Button onClick={onDone}>Done</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Select
        label="What kind of job"
        required
        options={DELIVERY_SERVICE_TYPES.map((t) => ({ value: t, label: t }))}
        value={form.serviceType}
        onChange={set('serviceType')}
        error={fieldErrors['serviceType']}
      />

      {/*
        Sender and receiver, in their own tinted panels.

        The colour is not decoration: the two halves hold the same two fields
        and a counter reading back a job needs to see at a glance which end it
        is looking at. The sender keeps writing to `customerName` — see
        migration 0111 — because renaming the column would break imports and
        the dispatch integration for the sake of a label.
      */}
      <Section tone="sender" title="Sender">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Sender name"
            required
            value={form.customerName}
            onChange={set('customerName')}
            error={fieldErrors['customerName']}
          />
          <Input
            label="Sender phone number"
            required
            inputMode="tel"
            placeholder="08030000000"
            value={form.customerPhone}
            onChange={set('customerPhone')}
            error={fieldErrors['customerPhone']}
          />
        </div>
      </Section>

      <Section tone="receiver" title="Receiver">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Receiver name"
            required
            value={form.receiverName}
            onChange={set('receiverName')}
            error={fieldErrors['receiverName']}
          />
          <Input
            label="Receiver phone number"
            required
            inputMode="tel"
            placeholder="08030000000"
            value={form.receiverPhone}
            onChange={set('receiverPhone')}
            error={fieldErrors['receiverPhone']}
          />
        </div>
      </Section>

      {offersZones && (
        <Select
          label="Zone"
          options={[{ value: '', label: 'No zone' }, ...zoneOptions]}
          value={form.zoneId}
          onChange={set('zoneId')}
          error={fieldErrors['zoneId']}
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Pick-up location"
          required
          value={form.pickup}
          onChange={set('pickup')}
          error={fieldErrors['pickup']}
        />
        <Input
          label="Drop-off location"
          required
          value={form.destination}
          onChange={set('destination')}
          error={fieldErrors['destination']}
        />
      </div>

      <Input
        label={`Delivery fee (${currency})`}
        required
        inputMode="decimal"
        placeholder="0.00"
        value={form.fee}
        onChange={set('fee')}
        error={fieldErrors['fee']}
        hint={priceHint}
      />

      {/*
        Asked only when the fee needs explaining: outside the approved price,
        nothing at all, or because the server said so. The reason goes with the
        fee to whoever approves it.
      */}
      {showPriceReason && (
        <div>
          {priceAsk && <p className="mb-1 text-xs text-amber-800">{priceAsk}</p>}
          <Textarea
            label="Reason for this fee"
            required
            rows={2}
            value={form.priceReason}
            onChange={set('priceReason')}
            error={fieldErrors['priceReason']}
            hint="Sent with the fee for approval. Nothing is booked until it is approved."
          />
        </div>
      )}

      {/* The split, shown as the fee is typed. Not a field — nobody decides it. */}
      {fee > 0 && (
        <p className="-mt-2 text-xs text-slate-500">
          Rider gets{' '}
          <span className="font-medium text-slate-700">
            {currencyPrefix(currency)}{riderPayout.toFixed(2)}
          </span>
          , business keeps{' '}
          <span className="font-medium text-slate-700">
            {currencyPrefix(currency)}{companyShare.toFixed(2)}
          </span>
        </p>
      )}

      <Section tone="rider" title="Rider">
      <RiderFields
        branchId={branchId}
        value={rider}
        onChange={(next, completion) => {
          setRider(next);
          setRiderCompletion(completion);
          setFieldErrors((e) => ({ ...e, riderId: '', riderExternalId: '', riderName: '', riderPhone: '' }));
        }}
        /*
          Compulsory only when a rider is actually being assigned — the
          statuses in RIDER_REQUIRED_STATUSES. A delivery can be recorded
          Awaiting Rider with the three boxes empty, and the rider added later
          by editing it; nothing is recreated and nothing is lost.

          When they ARE required, `RiderFields` still decides the PHONE on its
          own from the chosen rider's record: a rider the registry knows has no
          number yet may be assigned without one.
        */
        required={needsRider}
        errors={{
          riderId: fieldErrors['riderId'],
          riderExternalId: fieldErrors['riderExternalId'],
          riderName: fieldErrors['riderName'],
          riderPhone: fieldErrors['riderPhone'],
        }}
        current={existing ? initialRider : null}
      />
      </Section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Delivery status"
          required
          options={DELIVERY_STATUSES.map((s) => ({ value: s, label: s }))}
          value={form.deliveryStatus}
          onChange={set('deliveryStatus')}
          error={fieldErrors['deliveryStatus']}
        />
        <Select
          label="Payment status"
          required
          options={DELIVERY_PAYMENT_STATUSES.map((s) => ({ value: s, label: s }))}
          value={form.paymentStatus}
          onChange={set('paymentStatus')}
          error={fieldErrors['paymentStatus']}
        />
      </div>

      {/*
        Who owes the fee — NOT whether it has been paid.

        Three fields sit next to each other and they answer three different
        questions: `paidBy` says which end is billed, `paymentStatus` says
        whether the money has arrived, and `paymentMethod` says how. Choosing
        "Receiver" bills the receiver; it does not pay the bill, and nothing
        here touches payment status.
      */}
      <Select
        label="Paid by"
        required
        options={[
          { value: '', label: 'Choose who pays' },
          { value: 'sender', label: 'Sender' },
          { value: 'receiver', label: 'Receiver' },
        ]}
        value={form.paidBy}
        onChange={set('paidBy')}
        error={fieldErrors['paidBy']}
        hint="Who is billed for this delivery. Separate from whether it has been paid."
      />

      <Select
        label="How they paid"
        required
        options={DELIVERY_PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
        value={form.paymentMethod}
        onChange={set('paymentMethod')}
        error={fieldErrors['paymentMethod']}
      />

      {/* Only when a claim of payment needs evidence: cash reached a person. */}
      {needsProof && (
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Proof of payment
            <input
              type="file"
              accept="image/*,application/pdf"
              className="mt-1 block w-full text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
              }}
            />
          </label>
          <p className="mt-1 text-xs text-slate-500">
            {uploading
              ? 'Attaching…'
              : proofId
                ? 'Attached'
                : existing?.hasProof
                  ? 'Proof of payment is already on file. Choose a file only to replace it.'
                  : `A ${form.paymentMethod} payment leaves a record — attach the screenshot.`}
          </p>
          {fieldErrors['paymentProofAttachmentId'] && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors['paymentProofAttachmentId']}</p>
          )}
        </div>
      )}

      {isCancelled && (
        <Input
          label="Why was it cancelled"
          required
          value={form.cancelReason}
          onChange={set('cancelReason')}
          error={fieldErrors['cancelReason']}
        />
      )}

      {dateAsk !== null && (
        <div>
          <p className="mb-1 text-xs text-amber-800">{dateAsk}</p>
          <Textarea
            label="Reason for the earlier date"
            required
            rows={2}
            value={form.dateReason}
            onChange={set('dateReason')}
            error={fieldErrors['dateReason']}
          />
        </div>
      )}

      <Select
        label="Delivery type"
        value={form.deliveryType}
        onChange={(e) => setForm((f) => ({ ...f, deliveryType: e.target.value as '' | 'base' | 'fast' }))}
        options={[
          { value: '', label: 'Not stated' },
          { value: 'base', label: 'Base (standard)' },
          { value: 'fast', label: 'Fast (priority)' },
        ]}
      />

      <Input
        label="Dispatch job number (optional)"
        value={form.dispatchRef}
        onChange={set('dispatchRef')}
        error={fieldErrors['dispatchRef']}
        hint="The WhatsApp order number (CENO-…) or dispatch job, if there is one. It stops the same job being counted twice."
      />

      <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} />

      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending} disabled={uploading}>
          {existing ? 'Save changes' : 'Save delivery'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
